<script setup>
import { ref, onMounted, watch } from 'vue';
import apiFetch from '@wordpress/api-fetch';
import { useRouter } from 'vue-router';
import { clearUserEmail, getContactVerified, getUserEmail, setContactVerified, setUserEmail } from './utils/auth';
import { matomoTrackEvent } from './utils/matomo';
import ebusImg from './assets/ebus.png';
import { useUserStore } from './stores/user';

const router = useRouter();

const strings = ref(null);
const texts = ref([]);
const email = ref('');
const code = ref('');
const isLoading = ref(false);
const awaitingCode = ref(false);
const errorMessage = ref('');
const userStore = useUserStore();
const lang = document.documentElement.lang?.substring(0, 2);

onMounted(async () => {
  try {
    const [translations, landingPage] = await Promise.all([
      apiFetch({
        path: `/wp-json/ebus/translations?lang=${lang}`,
      }),
      apiFetch({
        path: `/wp-json/ebus/landing-page?lang=${lang}`,
      }),
    ]);

    strings.value = translations;
    texts.value = landingPage.texts;
  } catch (error) {
    console.error('Error: ', error);
  }
});

watch(isLoading, (value) => {
  document.documentElement.style.overflow = value ? 'hidden' : '';
}, { immediate: true });

async function handleSubmit() {
  email.value = email.value.trim();
  code.value = code.value.trim();

  if (!email.value || isLoading.value) return;

  matomoTrackEvent('contact-tool', 'ct_access_submit');

  isLoading.value = true;
  errorMessage.value = '';

  try {
    if (awaitingCode.value) {
      matomoTrackEvent('contact-tool', 'ct_submit_verification_code_login');

      const response = await apiFetch({
        path: '/wp-json/ebus/verify-code',
        method: 'POST',
        data: {
          email: email.value,
          code: code.value,
          lang,
        },
      });

      if (response.valid) {
        setUserEmail(email.value);
        setContactVerified(true);

        router.push('/contacts-list');
      } else {
        errorMessage.value = strings.value.invalid_code;
      }
    } else {
      if (getUserEmail() === email.value && getContactVerified()) {
        router.push('/contacts-list');
        return;
      }

      const response = await apiFetch({
        path: `/wp-json/ebus/check-user?email=${encodeURIComponent(email.value)}&lang=${lang}`,
      });

      if (response.exists) {
        setUserEmail(email.value);
        setContactVerified(false);

        awaitingCode.value = true;
        code.value = '';
      } else {
        clearUserEmail();

        userStore.email = email.value;
        router.push('/access-form');
      }
    }
  } catch (error) {
    console.log('Error: ', error);
  } finally {
    isLoading.value = false;
  }
}
</script>

<template>
  <section v-if="strings" class="relative! w-full! flex! flex-col! lg:flex-row! gap-y-8! lg:gap-y-0 bg-light-aqua! pt-0! min-h-screen">
    <div v-if="isLoading" class="fixed! inset-0! bg-gray/90! flex! items-center! justify-center! z-50!">
      <span class="w-20! h-20! border-4! border-aqua! border-t-transparent! rounded-full! animate-spin!"></span>
    </div>
    <div class="relative! w-full! lg:max-w-1/2 xl:max-w-[40%]! flex! flex-col! gap-y-8! lg:gap-y-0! px-6! pt-5.5! lg:pl-6! lg:pr-10! lg:pb-20!">
      <span class="hidden! lg:block! absolute! top-0! right-0! inset-y-0! w-3! bg-[linear-gradient(90deg,rgba(231,248,245,0),rgba(176,190,186,0.40))]!"></span>
      <h2 class="font-ibm-plex-mono! font-semibold! text-4xl! leading-tight! text-aqua! lg:mb-9.5!">
        {{ strings.ebus_contacts_database }}
      </h2>

      <div class="flex! flex-col! gap-y-5.5!">
        <div
          v-if="texts.length"
          v-for="(text, index) in texts"
          :key="`${index}-${text.title}`"
          class="flex! flex-col! gap-y-3.5!"
        >
          <h3 v-if="text.title" class="font-ibm-plex-mono! font-medium! text-2xl! leading-tight! text-black!">
            {{ text.title }}
          </h3>
          <div class="font-ibm-plex-mono! font-normal! text-base! leading-tight! text-black! wysiwyg" v-html="text.text">
          </div>
        </div>
      </div>
    </div>

    <div class="relative! w-full! lg:max-w-1/2 xl:max-w-[70%]! flex! flex-col! gap-y-4! lg:pl-12! lg:pr-0! lg:mt-26!">
      <h3 class="font-ibm-plex-mono! font-medium! text-2xl! leading-tight! text-black! px-6! lg:px-0!">
        {{ strings.access_contacts_database }}
      </h3>

      <div v-if="awaitingCode" class="w-full! max-w-100! font-ibm-plex-mono! font-normal! text-base! leading-tight! text-black! px-6! lg:px-0!">
        {{ strings.verification_code_sent }}
      </div>

      <form class="w-full! max-w-75! flex! flex-col! gap-y-5! px-6! lg:px-0!" @submit.prevent="handleSubmit">
        <div class="flex! flex-col! items-start! gap-y-6!">
          <input
            v-if="!awaitingCode"
            class="w-full! font-ibm-plex-mono! font-normal! text-base! text-black! placeholder:text-black! border-b-2! border-black! pb-2! disabled:opacity-60!"
            v-model="email"
            id="email"
            type="email"
            :placeholder="strings.email"
            :disabled="isLoading"
            required
          />

          <input
            v-if="awaitingCode"
            class="w-full! font-ibm-plex-mono! font-normal! text-base! text-black! placeholder:text-black! border-b-2! border-black! pb-2! disabled:opacity-60!"
            v-model="code"
            id="code"
            type="text"
            inputmode="numeric"
            pattern="[0-9]*"
            :placeholder="strings.code"
            :disabled="isLoading"
            required
          />

          <div v-if="errorMessage" class="font-ibm-plex-mono! font-normal! text-base! leading-tight! text-black!">
            {{ errorMessage }}
          </div>

          <button
            class="font-ibm-plex-mono! font-medium! text-base! leading-tight! text-black! py-1! px-3.75! border! border-black! rounded-full! transition-all hover:text-white! hover:bg-aqua! disabled:opacity-60! disabled:cursor-not-allowed!"
            type="submit"
            :disabled="isLoading"
          >
            {{ strings.submit }}
          </button>
        </div>
      </form>

      <div class="relative! w-full! overflow-hidden!">
        <img
          class="w-auto! max-w-157! md:max-w-274.5! object-contain! ml-auto! -mr-65! md:-mr-77! md:mt-20!"
          :src="ebusImg"
          alt="E-bus"
        >
      </div>
    </div>
  </section>
</template>
